//Customer booking view
import React, { useState, useEffect, useRef } from "react";
import SeatPicker from "./Config";
import "./styles.css";
import validateSeatSelectionNew from "./validateSeatSelectionNew";
import generateView, { buildAdjacencyMap, getBusStructure } from "./generateView";
import { toast, ToastContainer } from "react-toastify";
import jsPDF from "jspdf";
import LogoImage from "../../assets/images/logo.jpg";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";

import {
  saveBooking,
  searchBusRoute,
  updateBookings,
  cancelBookingHoldKeepalive,
  countBookedByExactMobile,
  buildRouteTimeContext,
  getRouteStopsSchedule,
} from "../../services/busServices";
import { markPaymentLifecycle } from "../../services/cybersourceService";
import { sendSMSNow } from "../../services/sendSMS";
import Loading from "../Utilities/Loading/Loading";
import { getSeatPriceLkr } from "../../config/bookingPrice";
import {
  applyLoyaltyDiscount,
  countMobileDigits,
  LOYALTY_LOOKUP_MIN_DIGITS,
} from "../../config/loyaltyDiscount";
import { resolvePickupArrivalTime, locationDisplayLabel } from "../../utils/routeStopSchedule";
import CybersourcePaymentModal from "../Payment/CybersourcePaymentModal";
import {
  validatePassengerDetails,
  validatePassengerEmail,
  validatePassengerMobile,
  validatePassengerName,
} from "../../utils/passengerValidation";
import Alert from "@mui/material/Alert";

function SeatGrid({ item, userMode, component, pickup, drop, date }) {
  const [selected, setSelected] = useState([]);
  const [seatGender, setSeatGender] = useState({});
  const [paid, setPaid] = useState(false);
  const [came, setCame] = useState(false);

  const [bookingDetails, setBookingDetails] = useState({
    nic: "",
    email: "",
    mobile_no: "",
    first_name: "",
    last_name: "",
    passenger_name: "",
    payment_method: "cybersource_hnb",
    payment_slip: "",
    from: pickup ?? "",
    to: drop ?? "",
    date: date ?? "",
    slip: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [previousBookedCount, setPreviousBookedCount] = useState(0);
  const loyaltyLookupRef = useRef(0);

  useEffect(() => {
    setBookingDetails((prev) => ({
      ...prev,
      ...(pickup != null && String(pickup).trim() !== "" ? { from: pickup } : {}),
      ...(drop != null && String(drop).trim() !== "" ? { to: drop } : {}),
      ...(date != null && String(date).trim() !== ""
        ? { date: String(date).trim() }
        : {}),
    }));
  }, [pickup, drop, date]);

  const resolveTripForSave = (detailsToSave) => {
    const from =
      pickup != null && String(pickup).trim() !== ""
        ? pickup
        : detailsToSave.from;
    const to =
      drop != null && String(drop).trim() !== "" ? drop : detailsToSave.to;
    const tripDate =
      date != null && String(date).trim() !== ""
        ? String(date).trim()
        : detailsToSave.date;
    return { from, to, date: tripDate };
  };

  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const [processingOpen, setProcessingOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [bookingErrorOpen, setBookingErrorOpen] = useState(false);
  const [bookingErrorMessage, setBookingErrorMessage] = useState("");
  const [cybersourcePaymentOpen, setCybersourcePaymentOpen] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const bookingIdRef = useRef(null);
  useEffect(() => {
    bookingIdRef.current = bookingId;
  }, [bookingId]);
  /** Live reserved seats from API; null means use `item` props (initial search). */
  const [reservedFromServer, setReservedFromServer] = useState(null);
  /** Bumps to remount SeatPicker so its internal selection matches parent after programmatic clears. */
  const [pickerResetKey, setPickerResetKey] = useState(0);

  useEffect(() => {
    setReservedFromServer(null);
  }, [item?.directus_id, item?.id]);

  const syncReservedSeatsFromApi = async () => {
    const trip = resolveTripForSave(bookingDetails);
    const { from, to, date: tripDate } = trip;
    if (!String(from || "").trim() || !String(to || "").trim() || !String(tripDate || "").trim()) {
      return;
    }
    if (!item) return;
    try {
      const buses = await searchBusRoute(from, to, tripDate);
      const match =
        buses.find((b) => b.directus_id && item.directus_id && b.directus_id === item.directus_id) ||
        buses.find(
          (b) => String(b.bus_number ?? b.id) === String(item.bus_number ?? item.id)
        );
      if (match) {
        setReservedFromServer({
          male: match.reservedSeatsByMale || [],
          female: match.reservedSeatsByFemale || [],
        });
      }
    } catch (e) {
      console.error("syncReservedSeatsFromApi:", e);
    }
  };

  const releasePendingPaymentBooking = async (lifecycle = {}) => {
    const id = bookingIdRef.current;
    if (id) {
      const paymentStatus = lifecycle.paymentStatus || "abandoned";
      const detail = lifecycle.detail || "user_cancel";
      try {
        await markPaymentLifecycle({
          bookingId: id,
          paymentStatus,
          detail,
          reference: lifecycle.reference || "",
          releaseSeats: true,
        });
      } catch (e) {
        console.error("Release pending payment booking:", e);
        try {
          await updateBookings({
            id,
            status: "cancelled",
            payment_status: paymentStatus,
            payment_outcome_detail: detail,
          });
        } catch (e2) {
          console.error("Fallback cancel booking:", e2);
        }
      }
    }
    setBookingId(null);
    setSelected([]);
    setSeatGender({});
    setPickerResetKey((k) => k + 1);
    await syncReservedSeatsFromApi();
  };

  const handleCardEntryTimeout = async () => {
    setCybersourcePaymentOpen(false);
    await releasePendingPaymentBooking({
      paymentStatus: "timed_out",
      detail: "card_entry_timeout",
    });
    toast.info("Payment time expired. Your seat hold was cancelled.", { position: "top-right" });
  };

  const handlePaymentModalClose = async (reason) => {
    setCybersourcePaymentOpen(false);
    if (reason === "success") return;
    const hadHold = Boolean(bookingIdRef.current);
    const detail =
      reason === "cancel"
        ? "user_cancel"
        : reason === "backdropClick" || reason === "escapeKeyDown"
          ? `dismiss_${reason}`
          : reason
            ? `dismiss_${reason}`
            : "dismiss";
    await releasePendingPaymentBooking({
      paymentStatus: "abandoned",
      detail,
    });
    if (hadHold) {
      toast.info("Payment cancelled. Your seat hold was released.", { position: "top-right" });
    }
  };

  useEffect(() => {
    if (!cybersourcePaymentOpen || !bookingId) return undefined;
    const id = bookingId;
    const onLeave = () => {
      cancelBookingHoldKeepalive(id);
    };
    window.addEventListener("pagehide", onLeave);
    window.addEventListener("beforeunload", onLeave);
    return () => {
      window.removeEventListener("pagehide", onLeave);
      window.removeEventListener("beforeunload", onLeave);
    };
  }, [cybersourcePaymentOpen, bookingId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingDetails((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "first_name" || name === "last_name") {
        next.passenger_name = [next.first_name, next.last_name]
          .map((s) => String(s || "").trim())
          .filter(Boolean)
          .join(" ");
      }
      return next;
    });
    // Clear or refresh field error while typing once the user has seen an error.
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      let msg = "";
      if (name === "first_name") msg = validatePassengerName(value, "First name");
      else if (name === "last_name") msg = validatePassengerName(value, "Last name");
      else if (name === "mobile_no") msg = validatePassengerMobile(value);
      else if (name === "email") msg = validatePassengerEmail(value, { required: true });
      else return prev;
      const next = { ...prev };
      if (msg) next[name] = msg;
      else delete next[name];
      return next;
    });
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    let msg = "";
    if (name === "first_name") msg = validatePassengerName(value, "First name");
    else if (name === "last_name") msg = validatePassengerName(value, "Last name");
    else if (name === "mobile_no") msg = validatePassengerMobile(value);
    else if (name === "email") msg = validatePassengerEmail(value, { required: true });
    else return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (msg) next[name] = msg;
      else delete next[name];
      return next;
    });
  };

  const reservedSeatsByMale =
    reservedFromServer?.male ?? item?.reservedSeatsByMale ?? [];
  const reservedSeatsByFemale =
    reservedFromServer?.female ?? item?.reservedSeatsByFemale ?? [];
  const counterSeats = item?.counter_seats ?? [];
  const BusStartTime = item?.depature_time ?? "";
  const BusStartFrom = item?.depature_from ?? "";
  const routeStops = getRouteStopsSchedule(item);
  const routeTimeContext = buildRouteTimeContext(item);

  const structure = getBusStructure(item);
  const generatedRows = generateView({
    structure,
    reservedSeatsByMale,
    reservedSeatsByFemale,
    removedSeats: structure.removed_seats ?? [],
    counterSeats,
  });
  const adjacencyMap = buildAdjacencyMap(generatedRows);

  const [loading, setLoading] = useState(false);
  const [multipleSeat, setMultipleSeat] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [buttonDisable, setButtonDisable] = useState(false);

  const price = getSeatPriceLkr(item);
  const subtotalPrice = price * selected.length;
  const loyalty = applyLoyaltyDiscount(subtotalPrice, previousBookedCount);
  const totalprice = loyalty.total;

  useEffect(() => {
    if (!userMode) return undefined;
    const mobile = String(bookingDetails.mobile_no || "").trim();
    const digits = countMobileDigits(mobile);
    if (digits < LOYALTY_LOOKUP_MIN_DIGITS) {
      setPreviousBookedCount(0);
      setLoyaltyLoading(false);
      return undefined;
    }
    const seq = ++loyaltyLookupRef.current;
    setLoyaltyLoading(true);
    const t = setTimeout(async () => {
      try {
        const count = await countBookedByExactMobile(mobile);
        if (loyaltyLookupRef.current !== seq) return;
        setPreviousBookedCount(count);
      } catch (err) {
        console.error("Loyalty mobile lookup failed:", err);
        if (loyaltyLookupRef.current !== seq) return;
        setPreviousBookedCount(0);
      } finally {
        if (loyaltyLookupRef.current === seq) setLoyaltyLoading(false);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [bookingDetails.mobile_no, userMode]);

  const handleChangeMultipleAllowed = (event) => {
    setSelected([]);
    setSeatGender({});
    setPickerResetKey((k) => k + 1);
    setMultipleSeat(event.target.checked);
  };

  const handleGenderChange = (event, seatNumber) => {
    const { value } = event.target;
    setSeatGender((prev) => ({ ...prev, [seatNumber]: value }));
  };

  const addSeatCallback = (row, number, id) => {
    setSelected((prev) => [...prev, number]);
  };

  const removeSeatCallback = (row, number, id) => {
    const { [number]: _, ...rest } = seatGender;
    setSeatGender(rest);
    setSelected((list) => list.filter((s) => s !== number));
  };

  const extractEnglishWord = locationDisplayLabel;

  const formatSeatGender = (sg) => {
    let result = "";
    for (const seat in sg) result += `\n\t\t${seat}(${sg[seat]})\n`;
    return result;
  };

  const generateBookingPDF = (ticketDetails = bookingDetails) =>
    new Promise((resolve, reject) => {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const fare = totalprice;
      const seatSummary = Object.entries(seatGender)
        .map(([seat, gender]) => `${seat} (${gender})`)
        .join(", ");
      const ticketId = `${item?.id || "BUS"}-${Date.now().toString().slice(-6)}`;
      const reachByTime = resolvePickupArrivalTime(
        routeStops,
        ticketDetails.from,
        BusStartTime,
        routeTimeContext,
      );

      const paint = (logoImg) => {
        doc.setFillColor(237, 244, 255);
        doc.rect(0, 0, pageWidth, pageHeight, "F");

        doc.setFillColor(13, 27, 42);
        doc.roundedRect(12, 12, pageWidth - 24, 36, 6, 6, "F");
        if (logoImg && logoImg.naturalWidth > 0) {
          doc.addImage(logoImg, "JPG", 18, 17, 20, 20);
        }
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(17);
        doc.text("VISHMITHA HIGHWAY EXPRESS", 44, 26);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text("Reference", pageWidth - 18, 20, { align: "right" });
        const ticketMeta = doc.splitTextToSize(ticketId, 38);
        doc.text(ticketMeta, pageWidth - 18, 25, { align: "right" });
        doc.setFontSize(9);
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 18, 39, { align: "right" });

        doc.setFillColor(255, 255, 255);
        doc.roundedRect(12, 56, pageWidth - 24, 54, 6, 6, "F");
        doc.setDrawColor(220, 229, 240);
        doc.roundedRect(12, 56, pageWidth - 24, 54, 6, 6, "S");
        doc.setTextColor(13, 27, 42);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("TRAVEL DETAILS", 18, 66);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        doc.text(`From: ${extractEnglishWord(ticketDetails.from) || ticketDetails.from}`, 18, 78);
        doc.text(`To: ${extractEnglishWord(ticketDetails.to) || ticketDetails.to}`, 18, 88);
        doc.text(`Date: ${ticketDetails.date}`, 18, 98);
        doc.text(`Reach by: ${reachByTime}`, 110, 78);
        doc.text(`Bus No: ${item?.bus_number || item?.id || "N/A"}`, 110, 88);
        doc.text(`Bus Type: ${item?.bus_type || "Standard"}`, 110, 98);

        doc.setFillColor(255, 255, 255);
        doc.roundedRect(12, 118, pageWidth - 24, 82, 6, 6, "F");
        doc.roundedRect(12, 118, pageWidth - 24, 82, 6, 6, "S");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("PASSENGER DETAILS", 18, 128);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        doc.text(`Passenger: ${ticketDetails.passenger_name}`, 18, 140);
        doc.text(`Mobile: ${ticketDetails.mobile_no}`, 18, 150);
        doc.text(`Seats: ${selected.length}`, 18, 160);
        const wrappedSeats = doc.splitTextToSize(`Seat Summary: ${seatSummary || "N/A"}`, pageWidth - 42);
        doc.text(wrappedSeats, 18, 170);

        doc.setFillColor(21, 101, 192);
        doc.roundedRect(pageWidth - 72, 134, 54, 44, 5, 5, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("TOTAL FARE", pageWidth - 45, 148, { align: "center" });
        doc.setFontSize(18);
        doc.text(`Rs ${fare.toLocaleString()}`, pageWidth - 45, 162, { align: "center" });

        doc.setDrawColor(180, 195, 214);
        doc.setLineDashPattern([2, 2], 0);
        doc.line(18, 214, pageWidth - 18, 214);
        doc.setLineDashPattern([], 0);
        doc.setTextColor(100, 116, 139);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text("Please keep this ticket until the journey is completed.", 18, 224);
        doc.text("Thank you for choosing Vishmitha Highway Express.", 18, 231);
        doc.text("Support: Booking verified against selected seats and route details.", 18, 238);
        doc.setFontSize(9);
        doc.text("System Generated E-Ticket", pageWidth - 18, 224, { align: "right" });
        doc.text("Powered by Metadew Technologies", pageWidth - 18, 231, { align: "right" });

        doc.save("booking_details.pdf");
      };

      const logo = new Image();
      logo.onload = () => {
        try {
          paint(logo);
          resolve();
        } catch (e) {
          reject(e);
        }
      };
      logo.onerror = () => {
        try {
          paint(null);
          resolve();
        } catch (e) {
          reject(e);
        }
      };
      logo.src = LogoImage;
    });

  const doSaveBooking = async (detailsToSave = bookingDetails) => {
    const trip = resolveTripForSave(detailsToSave);
    const mergedDetails = { ...detailsToSave, ...trip };
    const bookingPayload = {
      bus_number: item.id,
      ...mergedDetails,
      fee: totalprice,
      seats: [seatGender],
      status: "booked",
    };
    setLoading(true);
    setProcessingOpen(true);
    try {
      const result = await saveBooking(bookingPayload);
      if (!result.success) {
        setResponseMessage(result.message);
        setSelected([]);
        setSeatGender({});
        setBookingErrorMessage(result.message || "Selected seat is already booked.");
        setBookingErrorOpen(true);
        setLoading(false);
        setProcessingOpen(false);
        return;
      }
      setBookingDetails(mergedDetails);
      await generateBookingPDF(mergedDetails);
      await sendSMSNow(
        mergedDetails,
        totalprice,
        seatGender,
        BusStartTime,
        BusStartFrom,
        routeStops,
        routeTimeContext,
      );
      setProcessingOpen(false);
      setSuccessOpen(true);
    } catch (error) {
      console.error("Error saving booking:", error);
      setProcessingOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const validateBooking = async () => {
    if (selected.length <= 0) {
      toast.error("Please select one or more seats first", { position: "top-right" });
      return;
    }
    const firstSeatKey = Object.keys(seatGender)[0];
    const firstSeatGender = seatGender[firstSeatKey];
    if (firstSeatGender === undefined || Object.keys(seatGender).length !== selected.length) {
      toast.error("Gender selection is mandatory!", { position: "top-right" });
      return;
    }
    if (
      !bookingDetails.mobile_no?.trim() ||
      !bookingDetails.first_name?.trim() ||
      !bookingDetails.last_name?.trim() ||
      !bookingDetails.email?.trim()
    ) {
      setFieldErrors((prev) => ({
        ...prev,
        ...(!bookingDetails.first_name?.trim()
          ? { first_name: "First name is required" }
          : {}),
        ...(!bookingDetails.last_name?.trim()
          ? { last_name: "Last name is required" }
          : {}),
        ...(!bookingDetails.mobile_no?.trim()
          ? { mobile_no: "Mobile number is required" }
          : {}),
        ...(!bookingDetails.email?.trim() ? { email: "Email is required" } : {}),
      }));
      toast.error(
        "Please fill in all required fields (first name, last name, mobile, email)!",
        { position: "top-right" },
      );
      return;
    }

    const passengerCheck = validatePassengerDetails(bookingDetails, {
      emailRequired: true,
    });
    if (!passengerCheck.ok) {
      setFieldErrors(passengerCheck.errors);
      const firstMsg = Object.values(passengerCheck.errors)[0];
      toast.error(firstMsg || "Please fix the passenger details.", {
        position: "top-right",
      });
      return;
    }
    setFieldErrors({});

    if (!multipleSeat) {
      const res = validateSeatSelectionNew(
        firstSeatGender,
        selected,
        reservedSeatsByFemale,
        reservedSeatsByMale,
        adjacencyMap
      );
      if (!res) {
        toast.error(
          firstSeatGender === "male"
            ? "You can't select a seat near to the female"
            : "You can't select a seat near to the male",
          { position: "top-right" }
        );
        return;
      }
    }

    const updatedBookingDetails = { ...bookingDetails };

    // If payment method is cybersource, save booking first and then open payment modal
    if (bookingDetails.payment_method === "cybersource_hnb") {
      try {
        setLoading(true);
        setProcessingOpen(true);
        
        const trip = resolveTripForSave(updatedBookingDetails);
        const mergedDetails = { ...updatedBookingDetails, ...trip };
        const bookingPayload = {
          bus_number: item.id,
          ...mergedDetails,
          fee: totalprice,
          seats: [seatGender],
          status: "pending_payment", // Set to pending until payment is confirmed
          payment_status: "pending",
          payment_outcome_detail: "awaiting_customer_payment",
        };

        const result = await saveBooking(bookingPayload);
        setProcessingOpen(false);

        if (!result.success) {
          setResponseMessage(result.message);
          setSelected([]);
          setSeatGender({});
          setPickerResetKey((k) => k + 1);
          setBookingErrorMessage(result.message || "Selected seat is already booked.");
          setBookingErrorOpen(true);
          setLoading(false);
          void syncReservedSeatsFromApi();
          return;
        }

        setBookingId(result.bookingId || result.id || result.data?.id);
        setBookingDetails(mergedDetails);
        setLoading(false);

        // Open payment modal
        setCybersourcePaymentOpen(true);
      } catch (error) {
        console.error("Error creating booking:", error);
        setProcessingOpen(false);
        setLoading(false);
        toast.error("Error creating booking. Please try again.", { position: "top-right" });
        void syncReservedSeatsFromApi();
      }
      return;
    }

    setBookingDetails(updatedBookingDetails);
    await doSaveBooking(updatedBookingDetails);
  };

  const acceptPayement = () => {
    if (!paid) {
      setPaid(true);
      setCame(true);
      toast.success("Payment Accepted", { position: "top-right" });
    } else {
      setCame(true);
      toast.info("Participation Confirmed", { position: "top-right" });
    }
  };

  if (loading) return <Loading />;

  return (
    <Box>
      <ToastContainer />
      <Grid container spacing={4}>

        {/* Left column: seat picker */}
        <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {userMode && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                px: 2,
                py: 1,
                bgcolor: "#F0F4F8",
                borderRadius: 2,
                border: "1px solid rgba(0,0,0,0.06)",
                alignSelf: "flex-start",
              }}
            >
              <Typography variant="body2" fontWeight={600} color={!multipleSeat ? "#1565C0" : "text.secondary"}>
                Single Seat
              </Typography>
              <Switch
                checked={multipleSeat}
                onChange={handleChangeMultipleAllowed}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#1565C0" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#1565C0" },
                }}
              />
              <Typography variant="body2" fontWeight={600} color={multipleSeat ? "#1565C0" : "text.secondary"}>
                Multiple (max 5)
              </Typography>
            </Box>
          )}

          <SeatPicker
            key={`${pickerResetKey}-${multipleSeat}-${generatedRows.length}-${item?.directus_id ?? item?.bus_number ?? ""}`}
            addSeatCallback={addSeatCallback}
            removeSeatCallback={removeSeatCallback}
            rows={generatedRows}
            alpha
            maxReservableSeats={multipleSeat ? 5 : 1}
            visible
          />
        </Grid>

        {/* Right column: booking panel */}
        <Grid item xs={12} md={6}>

          {/* Price summary card */}
          <Box
            sx={{
              bgcolor: selected.length > 0 ? "#E3F2FD" : "#F8FAFF",
              border: "1px solid",
              borderColor: selected.length > 0 ? "#90CAF9" : "rgba(0,0,0,0.08)",
              borderRadius: 3,
              p: 2.5,
              mb: 3,
              transition: "all 0.25s ease",
            }}
          >
            {selected.length === 0 ? (
              <Typography variant="body2" color="text.disabled" textAlign="center" fontWeight={600}>
                Select a seat to see the price
              </Typography>
            ) : (
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                      {selected.length} Seat{selected.length > 1 ? "s" : ""} Selected
                    </Typography>
                    {loyalty.discountPercent > 0 ? (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textDecoration: "line-through" }}
                        >
                          Rs {loyalty.subtotal.toLocaleString()}
                        </Typography>
                        <Typography variant="h5" fontWeight={900} color="#1565C0">
                          Rs {totalprice.toLocaleString()}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="h5" fontWeight={900} color="#1565C0">
                        Rs {totalprice.toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={`Rs ${price} / seat`}
                    size="small"
                    sx={{ bgcolor: "#BBDEFB", color: "#1565C0", fontWeight: 700 }}
                  />
                </Stack>
                {loyalty.discountPercent > 0 && (
                  <Typography variant="caption" fontWeight={700} color="success.main">
                    Loyalty {loyalty.discountPercent}% off (− Rs {loyalty.discountAmount.toLocaleString()})
                  </Typography>
                )}
              </Stack>
            )}
          </Box>

          {/* Selected seats list */}
          {selected.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block", mb: 1 }}
              >
                Selected Seats
              </Typography>
              <List dense disablePadding>
                {selected.map((seat, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      bgcolor: "#F8FAFF",
                      border: "1px solid rgba(0,0,0,0.06)",
                      borderRadius: 2,
                      mb: 1,
                      px: 2,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <EventSeatIcon sx={{ color: "#1565C0", fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={700}>
                          Seat {seat}
                        </Typography>
                      }
                    />
                    {userMode ? (
                      <RadioGroup
                        row
                        name={`gender-${seat}`}
                        value={seatGender[seat] || ""}
                        onChange={(e) => handleGenderChange(e, seat)}
                      >
                        <FormControlLabel
                          value="male"
                          control={<Radio size="small" sx={{ color: "#1565C0", "&.Mui-checked": { color: "#1565C0" } }} />}
                          label={
                            <Stack direction="row" alignItems="center" spacing={0.3}>
                              <MaleIcon sx={{ fontSize: 15, color: "#1565C0" }} />
                              <Typography variant="caption" fontWeight={600}>Male</Typography>
                            </Stack>
                          }
                        />
                        <FormControlLabel
                          value="female"
                          control={<Radio size="small" sx={{ color: "#E91E96", "&.Mui-checked": { color: "#E91E96" } }} />}
                          label={
                            <Stack direction="row" alignItems="center" spacing={0.3}>
                              <FemaleIcon sx={{ fontSize: 15, color: "#E91E96" }} />
                              <Typography variant="caption" fontWeight={600}>Female</Typography>
                            </Stack>
                          }
                        />
                      </RadioGroup>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={acceptPayement}
                        disabled={came}
                        sx={{
                          borderRadius: 8,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          background: came ? undefined : "linear-gradient(135deg, #1565C0 0%, #0288D1 100%)",
                        }}
                      >
                        {!paid ? "Accept" : !came ? "Confirm" : "Confirmed"}
                      </Button>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Passenger details form */}
          {userMode && (
            <>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block", mb: 1.5 }}
              >
                Passenger Details
              </Typography>

              <TextField
                label="NIC"
                name="nic"
                fullWidth
                size="small"
                margin="dense"
                value={bookingDetails.nic}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                sx={{
                  mb: 0.8,
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
              />

              <Grid container spacing={1} sx={{ mb: 0.8 }}>
                <Grid item xs={6}>
                  <TextField
                    label="First Name"
                    name="first_name"
                    required
                    fullWidth
                    size="small"
                    margin="dense"
                    value={bookingDetails.first_name}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    error={Boolean(fieldErrors.first_name)}
                    helperText={fieldErrors.first_name || "Letters only — no numbers"}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon fontSize="small" sx={{ color: "#90A4AE" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Last Name"
                    name="last_name"
                    required
                    fullWidth
                    size="small"
                    margin="dense"
                    value={bookingDetails.last_name}
                    onChange={handleInputChange}
                    onBlur={handleFieldBlur}
                    error={Boolean(fieldErrors.last_name)}
                    helperText={fieldErrors.last_name || "Letters only — no numbers"}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>

              <TextField
                label="Mobile Number"
                name="mobile_no"
                required
                fullWidth
                size="small"
                margin="dense"
                value={bookingDetails.mobile_no}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={Boolean(fieldErrors.mobile_no)}
                helperText={
                  fieldErrors.mobile_no || "Digits only, optional + (e.g. 070xxxx88 or +9470xxxx588)"
                }
                inputProps={{ inputMode: "tel", autoComplete: "tel" }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" sx={{ color: "#90A4AE" }} />
                    </InputAdornment>
                  ),
                  endAdornment: loyaltyLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  mb: 0.8,
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
              />

              {countMobileDigits(bookingDetails.mobile_no) >= LOYALTY_LOOKUP_MIN_DIGITS &&
                !loyaltyLoading &&
                loyalty.discountPercent > 0 &&
                selected.length > 0 && (
                  <Alert severity="success" sx={{ mb: 1, borderRadius: 2 }}>
                    You have {loyalty.previousBookedCount} previous booking
                    {loyalty.previousBookedCount === 1 ? "" : "s"} — you got{" "}
                    <strong>{loyalty.discountPercent}% discount</strong>. Pay{" "}
                    <strong>Rs {totalprice.toLocaleString()}</strong>
                    {loyalty.subtotal !== totalprice
                      ? ` (was Rs ${loyalty.subtotal.toLocaleString()})`
                      : ""}
                    .
                  </Alert>
                )}

              {countMobileDigits(bookingDetails.mobile_no) >= LOYALTY_LOOKUP_MIN_DIGITS &&
                !loyaltyLoading &&
                loyalty.discountPercent === 0 &&
                previousBookedCount > 0 &&
                selected.length > 0 && (
                  <Alert severity="info" sx={{ mb: 1, borderRadius: 2 }}>
                    You have {previousBookedCount} previous booking
                    {previousBookedCount === 1 ? "" : "s"}. Loyalty discount starts at 5
                    confirmed trips.
                  </Alert>
                )}

              <TextField
                label="Email"
                name="email"
                required
                fullWidth
                size="small"
                margin="dense"
                type="email"
                value={bookingDetails.email}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email || "Email is mandatory (e.g. name@example.com)"}
                inputProps={{ inputMode: "email", autoComplete: "email" }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  mb: 0.8,
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
              />

              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block", mt: 2, mb: 1 }}
              >
                Payment Validation
              </Typography>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.08)",
                  bgcolor: "#F8FAFF",
                  mb: 1.5,
                }}
              >
                <Typography variant="body2" fontWeight={700} color="#0D1B2A" gutterBottom>
                  Online Payment
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Credit/Debit Card (Cybersource)
                </Typography>
              </Box>

              <FormControl component="fieldset" sx={{ mb: 1 }}>
                <FormLabel component="legend" sx={{ fontSize: 13, fontWeight: 700, color: "#64748B" }}>
                  Select Payment Method
                </FormLabel>
                <RadioGroup
                  name="payment_method"
                  value={bookingDetails.payment_method}
                  onChange={(e) =>
                    setBookingDetails((prev) => ({
                      ...prev,
                      payment_method: e.target.value,
                    }))
                  }
                  sx={{
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 0, sm: 1 },
                  }}
                >
                  <FormControlLabel
                    value="cybersource_hnb"
                    control={<Radio />}
                    label="Credit/Debit Card (Cybersource)"
                    sx={{ mr: { xs: 0, sm: 2 } }}
                  />
                </RadioGroup>
              </FormControl>

              {responseMessage && (
                <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 600 }}>
                  {responseMessage}
                </Typography>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={buttonDisable || selected.length === 0}
                onClick={async () => {
                  setButtonDisable(true);
                  await validateBooking();
                  setButtonDisable(false);
                }}
                sx={{
                  mt: 3,
                  py: 1.7,
                  borderRadius: 2,
                  fontWeight: 800,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  letterSpacing: 0.5,
                  lineHeight: 1.4,
                  whiteSpace: { xs: "normal", sm: "nowrap" },
                  background: "linear-gradient(135deg, #1565C0 0%, #0288D1 100%)",
                  boxShadow: "0 4px 20px rgba(21,101,192,0.4)",
                  "&:hover": {
                    boxShadow: "0 8px 28px rgba(21,101,192,0.55)",
                    transform: "translateY(-1px)",
                  },
                  "&:disabled": { opacity: 0.5 },
                  transition: "all 0.2s ease",
                }}
              >
                {selected.length === 0
                  ? "Select a Seat to Continue"
                  : `Pay Rs ${totalprice.toLocaleString()} · ${selected.length} Seat${selected.length > 1 ? "s" : ""}`}
              </Button>

              <Dialog
                open={cardDialogOpen}
                onClose={() => setCardDialogOpen(false)}
                fullWidth
                maxWidth="xs"
              >
                <DialogTitle sx={{ fontWeight: 700 }}>Pay by Card</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary">
                    This feature will be available soon. For now, please use Bank Transfer + Upload Slip.
                  </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button variant="contained" onClick={() => setCardDialogOpen(false)}>
                    OK
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Cybersource Payment Modal */}
              <CybersourcePaymentModal
                open={cybersourcePaymentOpen}
                onClose={handlePaymentModalClose}
                bookingDetails={{
                  ...bookingDetails,
                  id: bookingId,
                }}
                selectedSeats={selected}
                seatGender={seatGender}
                amount={totalprice}
                onCardEntryTimeout={handleCardEntryTimeout}
                onPaymentSuccess={async (paymentData) => {
                  const finalBookingDetails = {
                    ...bookingDetails,
                    payment_method: "cybersource_hnb",
                    payment_slip: paymentData.transactionId,
                  };

                  setBookingDetails(finalBookingDetails);

                  try {
                    await generateBookingPDF(finalBookingDetails);
                  } catch (pdfError) {
                    console.error("Error generating booking PDF:", pdfError);
                  }

                  try {
                    await sendSMSNow(
                      finalBookingDetails,
                      totalprice,
                      seatGender,
                      BusStartTime,
                      BusStartFrom,
                      routeStops,
                      routeTimeContext,
                    );
                  } catch (smsError) {
                    console.error("Error sending SMS after payment:", smsError);
                  }

                  toast.success("Payment successful! Booking completed.", {
                    position: "top-right",
                  });

                  setCybersourcePaymentOpen(false);
                  setSuccessOpen(true);
                }}
                onPaymentError={(errorMessage) => {
                  toast.error(`Payment failed: ${errorMessage}`, {
                    position: "top-right",
                  });
                  // Do not clear seats/bookingId here: the modal may still be open and
                  // `amount` comes from `totalprice` (price * selected.length). Clearing
                  // selection made Confirm Payment show LKR 0.00. User can fix card and retry.
                  void syncReservedSeatsFromApi();
                }}
              />

              {/* Processing overlay */}
              <Dialog open={processingOpen} fullWidth maxWidth="xs" disableEscapeKeyDown>
                <DialogContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 5,
                    gap: 2.5,
                  }}
                >
                  <CircularProgress size={56} thickness={4} sx={{ color: "#1565C0" }} />
                  <Typography variant="h6" fontWeight={700} color="#1565C0">
                    Your order is processing…
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Please wait while we confirm your booking.
                  </Typography>
                </DialogContent>
              </Dialog>

              {/* Success confirmation popup */}
              <Dialog open={successOpen} fullWidth maxWidth="xs">
                <DialogContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 5,
                    gap: 2,
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 72, color: "#22C55E" }} />
                  <Typography variant="h5" fontWeight={800} color="#1565C0" textAlign="center">
                    Booking Confirmed!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Your seats have been successfully booked. Your booking details PDF has been downloaded.
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Click below to return to the home page.
                  </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
                  <Button
                    variant="contained"
                    sx={{
                      px: 5,
                      fontWeight: 700,
                      background: "linear-gradient(135deg, #1565C0 0%, #0288D1 100%)",
                      borderRadius: 8,
                    }}
                    onClick={() => {
                      setSuccessOpen(false);
                      window.location.href = "/";
                    }}
                  >
                    Go to Home
                  </Button>
                </DialogActions>
              </Dialog>

              <Dialog open={bookingErrorOpen} fullWidth maxWidth="xs">
                <DialogContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 5,
                    gap: 2,
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 72, color: "#EF4444" }} />
                  <Typography variant="h5" fontWeight={800} color="#B91C1C" textAlign="center">
                    Seats Already Booked
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {bookingErrorMessage || "One or more selected seats are no longer available."}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Please select different seats and try again.
                  </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
                  <Button
                    variant="contained"
                    color="error"
                    sx={{ px: 5, fontWeight: 700, borderRadius: 8 }}
                    onClick={() => {
                      setBookingErrorOpen(false);
                      setBookingErrorMessage("");
                      setPickerResetKey((k) => k + 1);
                      void syncReservedSeatsFromApi();
                    }}
                  >
                    Select Again
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default SeatGrid;
